import { useCallback, useMemo, useState } from "react";
import type { AdoptedProposalGroup, ProposedStretch, TranscriptSpeaker } from "@/lib/api";
import { speakerColour, speakerName } from "@/lib/speakerColour";

/**
 * SPCH-13/14 — the parts a recording is about to be divided into, as the owner is editing them.
 *
 * The proposal is the machine's; this is the owner's draft over it. It starts as one part per
 * proposed stretch and changes by four gestures, each of which the proposal table and review
 * mode both offer: merge a part into the one above, restore the stretches a merge swallowed, cut
 * a new part at a line of the transcript, and move a part's start a line earlier or later. None
 * of them writes anything — the draft is adopted in one piece by "Start processing", which is
 * still the only write, and still all-or-nothing.
 *
 * **A part remembers the stretch it came from.** A part moved by a line still carries its
 * stretch's title, description and reason, so nudging a border does not throw away what the
 * `Reasoner` said about it. A part cut by hand in the middle of a stretch has none of its own
 * (`stretch: null`): copying the stretch's title onto both halves would give two meetings one
 * name, which is worse than one named and one waiting for a name.
 *
 * **It is kept per recording in this browser**, so leaving the table for review and coming back
 * — or reloading either — finds the draft where it was left. Keyed by the proposal's stretch
 * starts too: a re-run that proposes different stretches starts a fresh draft rather than
 * applying yesterday's cuts to today's borders.
 */
export interface DraftPart {
  /** The proposal stretch whose title, description and reason this part carries; null for a
   * part cut by hand. */
  stretch: number | null;
  /** Where the part starts when that is not its stretch's own start: a line cut or moved to by
   * hand. Always set when `stretch` is null. */
  cut: { sequence: number; atMs: number } | null;
  verdict: "meeting" | "discard";
  /** Null until the owner types in the field, so the default keeps tracking speaker names. */
  titleOverride: string | null;
  descriptionOverride: string | null;
}

/** A line of the transcript a part can start at: its sequence and when it starts. */
export interface CutPoint {
  sequence: number;
  atMs: number;
}

export type NameOf = (label: string) => { name: string; colour: string; confirmed: boolean };

// ------------------------------------------------------------------ the draft

/** One part per stretch, its verdict defaulted from the model's own guess — defaults count as
 * a verdict, so the draft is usable the moment it appears. */
export function initialParts(stretches: ProposedStretch[]): DraftPart[] {
  return stretches.map((stretch, index) => ({
    stretch: index,
    cut: null,
    verdict: stretch.kind === "meeting" ? "meeting" : "discard",
    titleOverride: null,
    descriptionOverride: null,
  }));
}

export function partStartMs(part: DraftPart, stretches: ProposedStretch[]): number {
  return part.cut?.atMs ?? stretches[part.stretch ?? 0]?.startMs ?? 0;
}

/** Where the recording the proposal covers ends. */
export function proposalEndMs(stretches: ProposedStretch[]): number {
  return stretches.reduce((end, stretch) => Math.max(end, stretch.endMs), 0);
}

/** Each part's span: from its own start to the next part's, the last to the proposal's end. */
export function partRanges(parts: DraftPart[], stretches: ProposedStretch[]): { startMs: number; endMs: number }[] {
  const end = proposalEndMs(stretches);
  return parts.map((part, position) => ({
    startMs: partStartMs(part, stretches),
    endMs: position + 1 < parts.length ? partStartMs(parts[position + 1]!, stretches) : end,
  }));
}

/** The stretch a moment falls in — the one whose reason and measurements a hand-cut part shows. */
export function stretchAt(stretches: ProposedStretch[], atMs: number): number {
  let found = 0;
  stretches.forEach((stretch, index) => {
    if (stretch.startMs <= atMs) found = index;
  });
  return found;
}

/** Merge a part into the one above it: its start goes, and with it its own title and verdict. */
export function mergeUp(parts: DraftPart[], position: number): DraftPart[] {
  if (position <= 0 || position >= parts.length) return parts;
  return [...parts.slice(0, position), ...parts.slice(position + 1)];
}

/**
 * Give a part back the proposal's own borders inside it — "Unmerge". Each stretch that starts
 * inside the part and is not already some part's stretch becomes a part again, as proposed.
 */
export function unmerge(parts: DraftPart[], position: number, stretches: ProposedStretch[]): DraftPart[] {
  const range = partRanges(parts, stretches)[position];
  if (!range) return parts;

  const taken = new Set(parts.map((part) => part.stretch));
  const starts = new Set(parts.map((part) => partStartMs(part, stretches)));
  const restored = initialParts(stretches).filter((part) => {
    const at = partStartMs(part, stretches);
    return !taken.has(part.stretch) && at > range.startMs && at < range.endMs && !starts.has(at);
  });

  return sortParts([...parts, ...restored], stretches);
}

/** How many of the proposal's stretches a part has taken in, itself included — "3 stretches
 * merged into one meeting". A stretch that starts inside the part but is still another part's
 * (its start moved later by hand) was not merged into anything. */
export function stretchesInside(parts: DraftPart[], position: number, stretches: ProposedStretch[]): number {
  const range = partRanges(parts, stretches)[position];
  if (!range) return 0;

  const others = new Set(parts.filter((_, index) => index !== position).map((part) => part.stretch));
  return stretches.filter(
    (stretch, index) =>
      !others.has(index)
      && (parts[position]!.stretch === index || (stretch.startMs >= range.startMs && stretch.startMs < range.endMs)),
  ).length;
}

/**
 * Cut a new part at a line: everything from it to the next part's start becomes a part of its
 * own, with the verdict of the part it was cut from and no name yet. Answers with where the new
 * part landed, or null when the line already starts a part or is the very start.
 */
export function cutAt(
  parts: DraftPart[],
  stretches: ProposedStretch[],
  point: CutPoint,
): { parts: DraftPart[]; position: number } | null {
  const ranges = partRanges(parts, stretches);
  const inside = ranges.findIndex((range) => point.atMs > range.startMs && point.atMs < range.endMs);
  if (inside < 0) return null;

  const added: DraftPart = {
    stretch: null,
    cut: { sequence: point.sequence, atMs: point.atMs },
    verdict: parts[inside]!.verdict,
    titleOverride: null,
    descriptionOverride: null,
  };

  return {
    parts: [...parts.slice(0, inside + 1), added, ...parts.slice(inside + 1)],
    position: inside + 1,
  };
}

/** Move a part's start to another line, strictly between its neighbours' starts. The first
 * part is the start of the recording and does not move. */
export function moveStart(
  parts: DraftPart[],
  position: number,
  stretches: ProposedStretch[],
  point: CutPoint,
): DraftPart[] {
  if (position <= 0 || position >= parts.length) return parts;

  const ranges = partRanges(parts, stretches);
  const previous = ranges[position - 1]!.startMs;
  const next = position + 1 < parts.length ? ranges[position + 1]!.startMs : Number.POSITIVE_INFINITY;
  if (point.atMs <= previous || point.atMs >= next) return parts;

  return parts.map((part, index) =>
    index === position ? { ...part, cut: { sequence: point.sequence, atMs: point.atMs } } : part,
  );
}

export function patchPart(parts: DraftPart[], position: number, change: Partial<DraftPart>): DraftPart[] {
  return parts.map((part, index) => (index === position ? { ...part, ...change } : part));
}

function sortParts(parts: DraftPart[], stretches: ProposedStretch[]): DraftPart[] {
  return [...parts].sort((a, b) => partStartMs(a, stretches) - partStartMs(b, stretches));
}

// ------------------------------------------------------------------ reading a part

export function partTitle(part: DraftPart, stretches: ProposedStretch[], nameOf: NameOf): string {
  if (part.titleOverride !== null) return part.titleOverride;
  return part.stretch === null ? "" : (withNames(stretches[part.stretch]?.title ?? null, nameOf) ?? "");
}

export function partDescription(part: DraftPart, stretches: ProposedStretch[], nameOf: NameOf): string {
  if (part.descriptionOverride !== null) return part.descriptionOverride;
  return part.stretch === null ? "" : (withNames(stretches[part.stretch]?.description ?? null, nameOf) ?? "");
}

/** What "Start processing" sends: where each part starts, and its words and verdict with the
 * names as they stand right now. */
export function adoptedGroups(parts: DraftPart[], stretches: ProposedStretch[], nameOf: NameOf): AdoptedProposalGroup[] {
  return parts.map((part, position) => {
    const words =
      part.verdict === "meeting"
        ? {
            title: partTitle(part, stretches, nameOf) || null,
            description: partDescription(part, stretches, nameOf) || null,
          }
        : { title: null, description: null };

    // The first part is the start of the recording whatever it says; the Server insists.
    const start = position === 0 ? { startIndex: 0 } : part.cut ? { fromSequence: part.cut.sequence } : { startIndex: part.stretch ?? 0 };

    return { ...start, ...words, kind: part.verdict };
  });
}

/**
 * Who talks in a part, as the voices the proposal step counted. A part whose borders are both
 * the proposal's own reads its stretches' voices exactly; a part with a border cut by hand reads
 * the transcript lines inside it when they are to hand, and the stretches it overlaps otherwise.
 */
export function partVoices(
  parts: DraftPart[],
  position: number,
  stretches: ProposedStretch[],
  lines?: { speakerId: string | null; startsAtMs: number | null; endsAtMs: number | null }[],
  speakers?: TranscriptSpeaker[],
): { speaker: string; speechMs: number }[] {
  const range = partRanges(parts, stretches)[position];
  if (!range) return [];

  const next = parts[position + 1];
  const byHand = parts[position]!.cut !== null || (next !== undefined && next.cut !== null);

  if (byHand && lines && speakers) {
    const labelOf = new Map(speakers.map((speaker) => [speaker.id, speaker.label]));
    const totals = new Map<string, number>();
    for (const line of lines) {
      const at = line.startsAtMs ?? 0;
      if (at < range.startMs || at >= range.endMs || line.speakerId === null) continue;
      const label = labelOf.get(line.speakerId);
      if (!label) continue;
      totals.set(label, (totals.get(label) ?? 0) + Math.max(0, (line.endsAtMs ?? at) - at));
    }
    return [...totals].map(([speaker, speechMs]) => ({ speaker, speechMs }));
  }

  return stretches
    .filter((stretch) => stretch.startMs < range.endMs && stretch.endMs > range.startMs)
    .flatMap((stretch) => stretch.voices);
}

// ------------------------------------------------------------------ names and sentences

/** A speaker's current name by diarizer label, and the colour they are drawn in elsewhere. */
export function speakerLookup(speakers: TranscriptSpeaker[]): NameOf {
  const byLabel = new Map<string, { name: string; colour: string; confirmed: boolean }>();
  speakers.forEach((speaker, index) => {
    if (speaker.label !== null)
      byLabel.set(speaker.label, {
        name: speakerName(speaker, index),
        colour: speakerColour(index),
        confirmed: speaker.status === "confirmed",
      });
  });

  return (label: string) => byLabel.get(label) ?? { name: `Speaker ${label}`, colour: "var(--text-muted)", confirmed: false };
}

/** `[S3]` in a title or description, replaced by whatever that voice is called right now. */
export function withNames(text: string | null, nameOf: (label: string) => { name: string }): string | null {
  return text === null ? null : text.replace(/\[S([^\]\s]+)\]/g, (_, label: string) => nameOf(label).name);
}

/**
 * Who talks, by the name each voice has right now, across every voice given. One person the
 * diarizer heard as several clusters — the same person in two rooms, typically, or two
 * merged stretches — is one row, with the shares added up. Voices under a twelfth of the
 * talk are left out.
 */
export function peopleIn(
  voices: { speaker: string; speechMs: number }[],
  nameOf: (label: string) => { name: string; colour: string },
): { name: string; colour: string; share: number }[] {
  const total = voices.reduce((sum, voice) => sum + voice.speechMs, 0);
  if (total === 0) return [];

  const people = new Map<string, { name: string; colour: string; share: number }>();
  for (const voice of voices) {
    const who = nameOf(voice.speaker);
    const person = people.get(who.name) ?? { name: who.name, colour: who.colour, share: 0 };
    person.share += voice.speechMs / total;
    people.set(who.name, person);
  }

  return [...people.values()].filter((person) => person.share >= 0.08).sort((a, b) => b.share - a.share);
}

/** A level in dBFS the way the page writes one, with a real minus sign. */
export function decibels(value: number): string {
  return `${Math.round(value)}`.replace("-", "−");
}

/** "1:07" — hours and minutes, which is the grain a day-long recording is divided at. */
export function clock(ms: number): string {
  const minutes = Math.round(ms / 60000);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}

/** Why a stretch starts where it does, as a sentence, from the numbers the step kept. */
export function borderSentence(
  stretch: ProposedStretch,
  previous: ProposedStretch | undefined,
  nameOf: (label: string) => { name: string; colour: string },
): string {
  const border = stretch.border;
  if (!border || !previous) return "Start of the recording.";

  // Said out loud beats measured: a line closing one meeting or opening the next is the reason.
  const closes = border.cues?.find((cue) => cue.kind === "closes");
  const opens = border.cues?.find((cue) => cue.kind === "opens");
  if (closes || opens) {
    const parts: string[] = [];
    if (closes) parts.push(`The previous meeting closes with “${closes.quote}”`);
    if (opens) parts.push(`${closes ? "this one opens" : "Opens"} with “${opens.quote}”`);
    return `${parts.join(", and ")}.`;
  }

  if (border.talkChanged)
    return stretch.talkSecondsPerMinute > previous.talkSecondsPerMinute ? "Talking starts." : "Talking stops.";

  const parts: string[] = [];
  if (border.roomChangeDb !== null && stretch.floorDb !== null && previous.floorDb !== null) {
    const direction = stretch.floorDb < previous.floorDb ? "quieter" : "louder";
    parts.push(`The room gets ${direction} (${decibels(previous.floorDb)} → ${decibels(stretch.floorDb)} dBFS)`);
  }

  // By name rather than by label: a person the diarizer heard as a new cluster in a new room
  // has not joined anything.
  const before = new Set(peopleIn(previous.voices, nameOf).map((person) => person.name));
  const after = new Set(peopleIn(stretch.voices, nameOf).map((person) => person.name));
  const joined = [...after].filter((name) => !before.has(name));
  const left = [...before].filter((name) => !after.has(name));

  if (joined.length > 0) parts.push(`${joined.join(", ")} ${joined.length === 1 ? "joins" : "join"}`);
  if (left.length > 0) parts.push(`${left.join(", ")} ${left.length === 1 ? "leaves" : "leave"}`);
  if (joined.length === 0 && left.length === 0) parts.push("the voices change");

  const sentence = parts.join(", and ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

/**
 * Why a part starts where it does. A proposal border keeps the proposal's reason; a border the
 * owner placed says so, because no measurement put it there.
 */
export function partReason(
  parts: DraftPart[],
  position: number,
  stretches: ProposedStretch[],
  nameOf: (label: string) => { name: string; colour: string },
): string {
  const part = parts[position]!;
  if (position === 0) return "Start of the recording.";
  if (part.stretch === null) return "You cut a new part here.";
  if (part.cut !== null) return `You moved this start from ${clock(stretches[part.stretch]!.startMs)}.`;
  return borderSentence(stretches[part.stretch]!, stretches[part.stretch - 1], nameOf);
}

/** The stretch whose measurements describe a part: its own, or the one a hand cut falls in. */
export function partStretch(part: DraftPart, stretches: ProposedStretch[]): ProposedStretch | undefined {
  return stretches[part.stretch ?? stretchAt(stretches, part.cut?.atMs ?? 0)];
}

// ------------------------------------------------------------------ keeping it

const STORAGE_PREFIX = "factorium.meetingParts.";

/** What a draft is a draft of: the proposal's stretch starts, in order. */
export function basisOf(stretches: ProposedStretch[]): string {
  return stretches.map((stretch) => stretch.startMs).join(",");
}

function isPart(value: unknown, stretchCount: number): value is DraftPart {
  if (typeof value !== "object" || value === null) return false;
  const part = value as Record<string, unknown>;
  const stretch = part["stretch"];
  const cut = part["cut"] as Record<string, unknown> | null | undefined;

  const stretchOk = stretch === null || (typeof stretch === "number" && stretch >= 0 && stretch < stretchCount);
  const cutOk = cut === null || (typeof cut === "object" && typeof cut["sequence"] === "number" && typeof cut["atMs"] === "number");
  const textOk = (field: unknown) => field === null || typeof field === "string";

  return (
    stretchOk
    && cutOk
    && (stretch !== null || cut !== null)
    && (part["verdict"] === "meeting" || part["verdict"] === "discard")
    && textOk(part["titleOverride"])
    && textOk(part["descriptionOverride"])
  );
}

/** The draft kept for this recording, or null when there is none for this proposal — or what is
 * kept cannot be read, which is treated the same: start again from the proposal. */
export function loadParts(recordingId: string, stretches: ProposedStretch[]): DraftPart[] | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + recordingId);
    if (!raw) return null;

    const kept = JSON.parse(raw) as { basis?: unknown; parts?: unknown };
    if (kept.basis !== basisOf(stretches) || !Array.isArray(kept.parts) || kept.parts.length === 0) return null;
    if (!kept.parts.every((part) => isPart(part, stretches.length))) return null;

    const parts = kept.parts as DraftPart[];
    return parts[0]!.stretch === 0 && parts[0]!.cut === null ? parts : null;
  } catch {
    return null;
  }
}

function saveParts(recordingId: string, stretches: ProposedStretch[], parts: DraftPart[]) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + recordingId, JSON.stringify({ basis: basisOf(stretches), parts }));
  } catch {
    // A browser that will not keep it still edits it; the draft lasts as long as the page.
  }
}

/**
 * The draft for one recording's proposal, shared by the table and review mode through the
 * browser's storage. `null` until there are stretches to draft over.
 */
export function useMeetingParts(recordingId: string | undefined, stretches: ProposedStretch[] | null) {
  const basis = stretches && stretches.length > 0 ? basisOf(stretches) : null;

  const kept = useMemo(
    () => (recordingId && stretches && basis ? (loadParts(recordingId, stretches) ?? initialParts(stretches)) : null),
    // The basis names the stretches that matter; a re-read of the same proposal is the same draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordingId, basis],
  );

  const [edited, setEdited] = useState<{ basis: string; parts: DraftPart[] } | null>(null);
  const parts = edited !== null && edited.basis === basis ? edited.parts : kept;

  const update = useCallback(
    (change: (parts: DraftPart[]) => DraftPart[]) => {
      if (!recordingId || !stretches || !basis) return;

      setEdited((previous) => {
        const base = previous !== null && previous.basis === basis ? previous.parts : kept;
        if (!base) return previous;

        const next = change(base);
        if (next === base) return previous;

        saveParts(recordingId, stretches, next);
        return { basis, parts: next };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordingId, basis, kept],
  );

  return { parts, update };
}
